import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Share2, Eye, Clock, Play } from 'lucide-react';

function VideoPlay() {
    const navigate = useNavigate();
    const location = useLocation();
    const video = location.state?.video;

    // If no video data, show error
    if (!video) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                    <div className="bg-red-600/20 border-2 border-red-600 rounded-lg p-6 mb-6">
                        <h2 className="text-red-400 text-xl sm:text-2xl font-bold mb-3">वीडियो नहीं मिला</h2>
                        <p className="text-gray-300 mb-4 text-sm sm:text-base">
                            कृपया वीडियो लिस्ट से कोई वीडियो चुनें।
                        </p>
                    </div>
                    
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-all font-semibold shadow-lg hover:shadow-xl"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        वापस जाएं
                    </button>
                </div>
            </div>
        );
    }

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({ 
                title: video.title, 
                url: video.videoUrl 
            });
        } else {
            navigator.clipboard.writeText(video.videoUrl);
            alert('लिंक कॉपी हो गया!');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
            {/* Header with Back Button */}
            <div className="bg-gradient-to-r from-gray-900 to-black shadow-lg sticky top-0 z-40 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-white hover:text-yellow-400 transition-colors font-semibold group"
                        >
                            <div className="bg-white/10 group-hover:bg-white/20 p-1.5 sm:p-2 rounded-lg transition-all">
                                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <span className="text-sm sm:text-base">वापस</span>
                        </button>
                        
                        <div className="flex-1 overflow-hidden">
                            <h1 className="text-white text-sm sm:text-lg md:text-xl font-bold truncate">
                                वीडियो प्लेयर
                            </h1>
                        </div>
                    </div>
                </div>
            </div>

            {/* Video Player Section */}
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
                <div className="bg-black rounded-lg overflow-hidden shadow-2xl">
                    {/* Video iframe - Responsive */}
                    <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                            className="absolute top-0 left-0 w-full h-full"
                            src={`${video.embedUrl}?autoplay=1&controls=1&modestbranding=1&rel=0`}
                            title={video.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        ></iframe>
                    </div>

                    {/* Video Info Section */}
                    <div className="p-4 sm:p-6 bg-gradient-to-r from-gray-900 to-black">
                        {/* Title */}
                        <h2 className="text-white text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4">
                            {video.title}
                        </h2>

                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                            {video.category && (
                                <span className="bg-red-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold uppercase text-white">
                                    {video.category}
                                </span>
                            )}
                            {video.views && (
                                <span className="flex items-center gap-1 sm:gap-2 text-gray-300 text-xs sm:text-sm">
                                    <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                                    {video.views}
                                </span>
                            )}
                            {video.timeAgo && (
                                <span className="flex items-center gap-1 sm:gap-2 text-gray-300 text-xs sm:text-sm">
                                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                    {video.timeAgo}
                                </span>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <a
                                href={video.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg hover:bg-red-700 transition-all font-semibold shadow-lg hover:shadow-xl hover:scale-105 text-sm sm:text-base"
                            >
                                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                                YouTube पर खोलें
                            </a>
                            <button 
                                onClick={handleShare}
                                className="flex-1 flex items-center justify-center gap-2 bg-white/10 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg hover:bg-white/20 transition-all font-semibold text-sm sm:text-base"
                            >
                                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                शेयर करें
                            </button>
                        </div>
                    </div>
                </div>

                {/* Additional Info Card (Optional) */}
                <div className="mt-4 sm:mt-6 bg-white/5 rounded-lg p-4 sm:p-6 backdrop-blur-sm">
                    <h3 className="text-white text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                        <Play className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                        वीडियो के बारे में
                    </h3>
                    <div className="space-y-2 sm:space-y-3 text-gray-300 text-sm sm:text-base">
                        <p className="flex items-start sm:items-center gap-2">
                            <span className="text-gray-400 font-semibold min-w-[80px] sm:min-w-[100px]">शीर्षक:</span>
                            <span>{video.title}</span>
                        </p>
                        {video.category && (
                            <p className="flex items-center gap-2">
                                <span className="text-gray-400 font-semibold min-w-[80px] sm:min-w-[100px]">श्रेणी:</span>
                                <span>{video.category}</span>
                            </p>
                        )}
                        {video.duration && (
                            <p className="flex items-center gap-2">
                                <span className="text-gray-400 font-semibold min-w-[80px] sm:min-w-[100px]">अवधि:</span>
                                <span>{video.duration}</span>
                            </p>
                        )}
                        {video.views && (
                            <p className="flex items-center gap-2">
                                <span className="text-gray-400 font-semibold min-w-[80px] sm:min-w-[100px]">व्यूज:</span>
                                <span>{video.views}</span>
                            </p>
                        )}
                        {video.timeAgo && (
                            <p className="flex items-center gap-2">
                                <span className="text-gray-400 font-semibold min-w-[80px] sm:min-w-[100px]">अपलोड:</span>
                                <span>{video.timeAgo}</span>
                            </p>
                        )}
                    </div>
                </div>

                {/* Back Button at Bottom */}
                <div className="mt-6 sm:mt-8 text-center">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 bg-white/10 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg hover:bg-white/20 transition-all font-semibold shadow-lg hover:shadow-xl text-sm sm:text-base"
                    >
                        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        सभी वीडियो देखें
                    </button>
                </div>
            </div>
        </div>
    );
}

export default VideoPlay;